import { Timestamp } from '../firebase';
import { normalizeItemColor, DEFAULT_ITEM_COLOR } from './itemColors';

/** Helpers for calendar events (legacy `date` + new `startAt`/`endAt` Timestamps). */

export function toYmd(d) {
  const x = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}

function atDayStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * @returns {{ start: Date, end: Date, allDay: boolean } | null}
 */
export function getEventWindow(ev) {
  if (ev.startAt?.toDate) {
    const s = ev.startAt.toDate();
    const e = ev.endAt?.toDate?.() ? ev.endAt.toDate() : s;
    return { start: s, end: e >= s ? e : s, allDay: !!ev.allDay };
  }
  if (ev.date) {
    const p = ev.date.split('-').map(Number);
    const s = new Date(p[0], p[1] - 1, p[2]);
    const endStr = ev.endDate || ev.date;
    const p2 = endStr.split('-').map(Number);
    const e = new Date(p2[0], p2[1] - 1, p2[2]);
    return { start: s, end: e >= s ? e : s, allDay: true };
  }
  return null;
}

export function eventSortKey(ev) {
  const w = getEventWindow(ev);
  if (!w) return 0;
  return w.start.getTime();
}

/**
 * Which days in `viewMonth` does this event appear on (incl. recurrence expansion).
 * @param {object} ev Firestore event doc data (+ _id)
 * @param {Date} viewMonth Any date inside the month to show
 * @returns {Set<string>} YYYY-MM-DD
 */
export function getYmdsForEventInMonth(ev, viewMonth) {
  const win = getEventWindow(ev);
  if (!win) return new Set();

  const y = viewMonth.getFullYear();
  const m = viewMonth.getMonth();
  const monthStart = new Date(y, m, 1);
  const monthEnd = new Date(y, m + 1, 0, 23, 59, 59, 999);

  const until = ev.recurrenceUntil?.toDate?.()
    ? ev.recurrenceUntil.toDate()
    : new Date(2099, 11, 31);
  const recurrence = ev.recurrence && ev.recurrence !== 'none' ? ev.recurrence : 'none';
  const maxCount =
    typeof ev.recurrenceCount === 'number' && ev.recurrenceCount >= 1
      ? Math.min(500, Math.floor(ev.recurrenceCount))
      : null;

  const out = new Set();

  if (recurrence === 'none') {
    let cur = atDayStart(win.start);
    const last = atDayStart(win.end);
    while (cur <= last) {
      if (cur >= monthStart && cur <= monthEnd) out.add(toYmd(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  const startDay = atDayStart(win.start);
  const startDayMs = startDay.getTime();

  if (recurrence === 'daily') {
    let occ = 0;
    const cur = new Date(startDay);
    while (cur <= until) {
      if (maxCount && occ >= maxCount) break;
      if (cur >= monthStart && cur <= monthEnd) out.add(toYmd(cur));
      occ += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  if (recurrence === 'weekly') {
    let occ = 0;
    const cur = new Date(startDay);
    while (cur <= until) {
      if (maxCount && occ >= maxCount) break;
      if (cur >= monthStart && cur <= monthEnd) out.add(toYmd(cur));
      occ += 1;
      cur.setDate(cur.getDate() + 7);
    }
    return out;
  }

  if (recurrence === 'monthly') {
    const base = win.start;
    for (let occ = 0; ; occ += 1) {
      if (maxCount && occ >= maxCount) break;
      const cand = new Date(base.getFullYear(), base.getMonth() + occ, base.getDate());
      if (cand.getDate() !== base.getDate()) continue;
      if (atDayStart(cand).getTime() < startDayMs) continue;
      if (cand > until) break;
      if (cand >= monthStart && cand <= monthEnd) out.add(toYmd(cand));
    }
    return out;
  }

  if (recurrence === 'yearly') {
    const base = win.start;
    for (let occ = 0; ; occ += 1) {
      if (maxCount && occ >= maxCount) break;
      const cand = new Date(base.getFullYear() + occ, base.getMonth(), base.getDate());
      if (cand.getMonth() !== base.getMonth() || cand.getDate() !== base.getDate()) continue;
      if (atDayStart(cand).getTime() < startDayMs) continue;
      if (cand > until) break;
      if (cand >= monthStart && cand <= monthEnd) out.add(toYmd(cand));
    }
    return out;
  }

  return out;
}

/** Human-readable recurrence line for cards (pass `t` from i18n). */
export function formatRecurrenceLine(ev, t) {
  const rec = ev.recurrence && ev.recurrence !== 'none' ? ev.recurrence : 'none';
  if (rec === 'none') return '';
  const label = t(`calendarRecurrence${rec[0].toUpperCase()}${rec.slice(1)}`);
  const n = typeof ev.recurrenceCount === 'number' && ev.recurrenceCount >= 1 ? ev.recurrenceCount : null;
  if (n) return t('calendarRecurrenceLineCount', { rule: label, count: n });
  if (ev.recurrenceUntil?.toDate) {
    return t('calendarRecurrenceLineUntil', {
      rule: label,
      date: ev.recurrenceUntil.toDate().toLocaleDateString(),
    });
  }
  return label;
}

/** Human-readable server-driven reminder line (uses i18n `t`). */
export function formatCalendarReminderSummary(ev, t) {
  if (
    typeof ev.reminderBeforeStartMinutes !== 'number' ||
    !Number.isFinite(ev.reminderBeforeStartMinutes)
  ) {
    return null;
  }
  const m = ev.reminderBeforeStartMinutes;
  if (m === 0) return t('reminderSummaryAtEventStart');
  return t('reminderSummaryNBeforeEvent', { n: m });
}

export function formatEventWhen(ev, locale) {
  const w = getEventWindow(ev);
  if (!w) return ev.date || '';
  const loc = locale || undefined;
  if (w.allDay) {
    if (toYmd(w.start) === toYmd(w.end)) return w.start.toLocaleDateString(loc);
    return `${w.start.toLocaleDateString(loc)} – ${w.end.toLocaleDateString(loc)}`;
  }
  return `${w.start.toLocaleString(loc)} – ${w.end.toLocaleString(loc)}`;
}

export function toDatetimeLocalValue(d) {
  const x = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

/** Default form draft for a given calendar day (YYYY-MM-DD). */
export function defaultCalendarDraft(ymd) {
  const y = ymd || toYmd(new Date());
  return {
    allDay: true,
    startDate: y,
    endDate: y,
    startDatetime: `${y}T09:00`,
    endDatetime: `${y}T10:00`,
    location: '',
    notes: '',
    recurrence: 'none',
    recurrenceUntil: '',
    recurrenceEndMode: 'date',
    recurrenceCount: '',
    color: DEFAULT_ITEM_COLOR,
    reminderBeforeStart: '',
    reminderBeforeCustom: '',
  };
}

/**
 * @param {object} form — title, allDay, start/end dates or datetimes, recurrence, recurrenceUntil, location
 */
export function validateCalendarForm(form) {
  if (!form.title?.trim()) return false;
  if (form.allDay) {
    if (!form.startDate) return false;
    const end = form.endDate || form.startDate;
    if (end < form.startDate) return false;
  } else {
    if (!form.startDatetime || !form.endDatetime) return false;
    if (new Date(form.endDatetime) < new Date(form.startDatetime)) return false;
  }
  const rec = form.recurrence && form.recurrence !== 'none' ? form.recurrence : 'none';
  if (rec !== 'none') {
    const mode = form.recurrenceEndMode === 'count' ? 'count' : 'date';
    if (mode === 'count') {
      const n = parseInt(String(form.recurrenceCount ?? '').trim(), 10);
      if (!Number.isFinite(n) || n < 1 || n > 500) return false;
    } else {
      if (!form.recurrenceUntil) return false;
      const until = new Date(`${form.recurrenceUntil}T23:59:59.999`);
      const startMs = form.allDay
        ? new Date(`${form.startDate}T00:00:00`).getTime()
        : new Date(form.startDatetime).getTime();
      const startDay = atDayStart(new Date(startMs));
      if (until < startDay) return false;
    }
  }
  return true;
}

/**
 * Firestore fields: allDay, startAt, endAt, date, optional endDate, recurrence, optional recurrenceUntil, optional location.
 */
export function buildCalendarEventFields(form) {
  const recurrence = form.recurrence && form.recurrence !== 'none' ? form.recurrence : 'none';
  const allDay = !!form.allDay;
  let startAt;
  let endAt;
  let dateYmd;
  let endDateYmd;

  if (allDay) {
    const s = form.startDate;
    const e = form.endDate || form.startDate;
    startAt = Timestamp.fromDate(new Date(`${s}T00:00:00`));
    endAt = Timestamp.fromDate(new Date(`${e}T23:59:59.999`));
    dateYmd = s;
    endDateYmd = e;
  } else {
    const sd = new Date(form.startDatetime);
    const ed = new Date(form.endDatetime);
    startAt = Timestamp.fromDate(sd);
    endAt = Timestamp.fromDate(ed);
    dateYmd = toYmd(sd);
    endDateYmd = toYmd(ed);
  }

  const out = {
    allDay,
    startAt,
    endAt,
    date: dateYmd,
    recurrence,
    color: normalizeItemColor(form.color),
  };
  if (endDateYmd !== dateYmd) out.endDate = endDateYmd;
  if (recurrence !== 'none') {
    const mode = form.recurrenceEndMode === 'count' ? 'count' : 'date';
    if (mode === 'count') {
      const n = parseInt(String(form.recurrenceCount ?? '').trim(), 10);
      if (Number.isFinite(n) && n >= 1) out.recurrenceCount = Math.min(500, n);
    } else if (form.recurrenceUntil) {
      out.recurrenceUntil = Timestamp.fromDate(new Date(`${form.recurrenceUntil}T23:59:59.999`));
    }
  }
  const loc = form.location != null ? String(form.location).trim() : '';
  if (loc) out.location = loc;
  const notes = form.notes != null ? String(form.notes).trim().slice(0, 8000) : '';
  if (notes) out.notes = notes;
  const rb = form.reminderBeforeStart;
  if (rb !== undefined && rb !== null && String(rb).trim() !== '') {
    const n = parseInt(String(rb).trim(), 10);
    if (Number.isFinite(n) && n >= 0) out.reminderBeforeStartMinutes = n;
  }
  return out;
}
